"use client";

import Image from "next/image";
import { ChevronDown, Zap } from "lucide-react";
import { useId, useState } from "react";

import { CcButton } from "@/components/ui/CcButton";
import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import type { AiAgentsSubpageCopy } from "./ai-agents-page-copy";

type CcAgentIndustriesProps = {
  copy: AiAgentsSubpageCopy["industries"];
};

/**
 * Example agents per industry, as twin.so's fill-in sentence: "We work in
 * [industry] and the first job we'd hand over is [job]". Every example is
 * in the HTML; the choices only decide which one shows.
 */
export function CcAgentIndustries({ copy }: CcAgentIndustriesProps) {
  const id = useId();
  const [industryIndex, setIndustryIndex] = useState(0);
  const [jobIndex, setJobIndex] = useState(0);
  const industry = copy.items[industryIndex];

  return (
    <section
      className="cc-section cc-agent-industries"
      id="ai-agent-examples"
      aria-labelledby="cc-agent-industries-title"
      data-cc-nav="light"
      {...devSectionAttr("ai-agent-examples")}
    >
      <div className="cc-agent-industries__container">
        <header className="cc-agent-industries__header">
          <CcSanjayaEyebrow theme="on-subtle">{copy.eyebrow}</CcSanjayaEyebrow>
          <h2 id="cc-agent-industries-title" className="cc-agent-industries__title">
            {copy.title}
          </h2>
          <p className="cc-agent-industries__intro">{copy.intro}</p>
        </header>

        <p className="cc-agent-industries__sentence">
          <span>{copy.sentenceA}</span>
          <label className="cc-agent-select">
            <span className="sr-only">{copy.sentenceA}</span>
            <select
              id={`${id}-industry`}
              value={industryIndex}
              onChange={(event) => {
                setIndustryIndex(Number(event.target.value));
                setJobIndex(0);
              }}
            >
              {copy.items.map((item, index) => (
                <option key={item.id} value={index}>
                  {item.label}
                </option>
              ))}
            </select>
            <ChevronDown size={18} strokeWidth={2.4} aria-hidden="true" />
          </label>
          <span>{copy.sentenceB}</span>
          <label className="cc-agent-select">
            <span className="sr-only">{copy.sentenceB}</span>
            <select id={`${id}-job`} value={jobIndex} onChange={(event) => setJobIndex(Number(event.target.value))}>
              {industry.jobs.map((job, index) => (
                <option key={job.id} value={index}>
                  {job.label}
                </option>
              ))}
            </select>
            <ChevronDown size={18} strokeWidth={2.4} aria-hidden="true" />
          </label>
        </p>

        {copy.items.map((item, itemIndex) => (
          <div key={item.id} className="cc-agent-industries__panel" hidden={itemIndex !== industryIndex}>
            <div className="cc-agent-industries__photo">
              <Image
                src={item.photo.src}
                alt={item.photo.alt}
                width={item.photo.width}
                height={item.photo.height}
                sizes="(min-width: 1200px) 520px, (min-width: 810px) 45vw, 92vw"
                quality={75}
                loading="lazy"
              />
            </div>
            <div className="cc-agent-industries__jobs">
              {item.jobs.map((job, index) => (
                <article
                  key={job.id}
                  className="cc-agent-job"
                  hidden={itemIndex === industryIndex && index !== jobIndex}
                >
                  <h3>{job.label}</h3>
                  <p>{job.text}</p>
                  <dl>
                    <div>
                      <dt>{copy.triggerLabel}</dt>
                      <dd>
                        <Zap size={12} strokeWidth={2.4} aria-hidden="true" />
                        {job.trigger}
                      </dd>
                    </div>
                    <div>
                      <dt>{copy.toolsLabel}</dt>
                      <dd className="cc-agent-job__tools">
                        {job.tools.map((logo) => (
                          <span key={logo}>
                            <Image src={logo} alt="" width={18} height={18} unoptimized />
                          </span>
                        ))}
                      </dd>
                    </div>
                  </dl>
                </article>
              ))}
              <CcButton bookCallPopup bookCallVariant="ai-agents" label={copy.cta} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
